import React from 'react';
import { formatMessage } from 'umi';

import {
  AuditOutlined,
  ClockCircleOutlined,
  DeleteFilled,
  DownloadOutlined,
  EyeFilled,
  FormOutlined,
  PlayCircleOutlined,
  SettingFilled,
  StarFilled,
  StarOutlined,
  SyncOutlined,
} from '@ant-design/icons';

import { CoreEnums } from '@/utils/domain/constants';

export const createStartItem = (onStart) => ({
  key: 'start',
  label: formatMessage({ id: 'global.text.start' }),
  icon: <PlayCircleOutlined />,
  onClick: (e) => {
    if (e?.preventDefault) {
      e.preventDefault();
      e.stopPropagation();
    }
    onStart?.(e);
  },
  dataActive: true,
});

export const createStartModeItem = (startMode, onStartModeClick, startModeLabel) => {
  const START_MODE_CONFIG = {
    [CoreEnums.ProcessStartMode.Instant]: {
      key: 'startMode',
      label: formatMessage({ id: 'global.text.start' }),
      icon: <PlayCircleOutlined />,
      onClick: (e) => {
        if (e?.preventDefault) {
          e.preventDefault();
          e.stopPropagation();
        }
        onStartModeClick?.(e);
      },
      dataActive: true,
    },
    [CoreEnums.ProcessStartMode.Automated]: {
      key: 'startMode',
      label: startModeLabel || formatMessage({ id: 'global.text.automated' }),
      icon: <SyncOutlined />,
      disabled: true,
      dataDisabled: true,
    },
    [CoreEnums.ProcessStartMode.Scheduled]: {
      key: 'startMode',
      label: startModeLabel || formatMessage({ id: 'global.text.scheduled' }),
      icon: <ClockCircleOutlined />,
      disabled: true,
      dataDisabled: true,
    },
  };

  return START_MODE_CONFIG[startMode] || null;
};

export const createFavoriteItem = (onFavorite, isFavorite) => ({
  key: 'favorite',
  label: formatMessage({ id: 'global.text.favorite' }),
  icon: isFavorite ? <StarFilled /> : <StarOutlined />,
  onClick: (e) => {
    if (e?.preventDefault) {
      e.preventDefault();
      e.stopPropagation();
    }
    onFavorite?.(e);
  },
  dataActive: isFavorite,
});

export const createTaskTracesItem = (onTaskTraces) => ({
  key: 'taskTraces',
  label: formatMessage({ id: 'customList.listTaskTrace.title' }),
  icon: <AuditOutlined />,
  onClick: (e) => {
    if (e?.preventDefault) {
      e.preventDefault();
      e.stopPropagation();
    }
    onTaskTraces?.(e);
  },
});

export const createConfigItem = (onConfig, disabled, customLabel) => ({
  key: 'config',
  label: customLabel || formatMessage({ id: 'global.text.config' }),
  icon: <SettingFilled />,
  onClick: (e) => {
    if (e?.preventDefault) {
      e.preventDefault();
      e.stopPropagation();
    }
    onConfig?.(e);
  },
  disabled: disabled,
  dataDisabled: disabled,
});

export const createProcessConfigItem = (onProcessConfig, disabled) => ({
  key: 'processConfig',
  label: formatMessage({ id: 'global.text.processConfig' }),
  icon: <SettingFilled />,
  onClick: (e) => {
    if (e?.preventDefault) {
      e.preventDefault();
      e.stopPropagation();
    }
    onProcessConfig?.(e);
  },
  disabled: disabled,
  dataDisabled: disabled,
});

export const createDetailItem = (onDetail) => ({
  key: 'detail',
  label: formatMessage({ id: 'global.text.detail' }),
  icon: <EyeFilled />,
  onClick: (e) => {
    if (e?.preventDefault) {
      e.preventDefault();
      e.stopPropagation();
    }
    onDetail?.(e);
  },
});

export const createEditItem = (onEdit, disabled) => ({
  key: 'edit',
  label: formatMessage({ id: 'global.text.edit' }),
  icon: <FormOutlined />,
  onClick: (e) => {
    if (e?.preventDefault) {
      e.preventDefault();
      e.stopPropagation();
    }
    onEdit?.(e);
  },
  disabled: disabled,
  dataDisabled: disabled,
});

export const createDeleteItem = (onRemove, disabled) => ({
  key: 'delete',
  label: formatMessage({ id: 'global.text.delete' }),
  icon: <DeleteFilled />,
  onClick: (e) => {
    if (e?.preventDefault) {
      e.preventDefault();
      e.stopPropagation();
    }
    onRemove?.(e);
  },
  disabled: disabled,
  dataDisabled: disabled,
});

export const createBulkDownloadItem = (onBulkDownload) => ({
  key: 'bulkDownload',
  label: formatMessage({ id: 'process.text.bulk.download' }),
  icon: <DownloadOutlined />,
  onClick: (e) => {
    if (e?.preventDefault) {
      e.preventDefault();
      e.stopPropagation();
    }
    onBulkDownload?.(e);
  },
  dataActive: true,
});

export const DEFAULT_ACTION_ORDER = {
  primary: ['start', 'startMode', 'favorite', 'taskTraces', 'processConfig'],
  secondary: ['config', 'detail', 'edit', 'bulkUpload', 'bulkDownload', 'delete'],
};

const sortByOrder = (items, orderArray) => {
  const itemMap = new Map(items.map((item) => [item.key, item]));
  const orderedItems = [];
  const remainingItems = new Set(items);

  // Add items in order specified by config
  orderArray.forEach((key) => {
    const item = itemMap.get(key);
    if (item) {
      orderedItems.push(item);
      remainingItems.delete(item);
    }
  });

  // Append remaining items that weren't in config
  remainingItems.forEach((item) => {
    orderedItems.push(item);
  });

  return orderedItems;
};

export const separatePriorityItems = (items, actionOrder = DEFAULT_ACTION_ORDER) => {
  const priorityItems = [];
  const dropdownItems = [];

  const primaryKeys = actionOrder.primary || DEFAULT_ACTION_ORDER.primary;
  const secondaryKeys = actionOrder.secondary || DEFAULT_ACTION_ORDER.secondary;

  items.forEach((item) => {
    if (primaryKeys.includes(item.key)) {
      priorityItems.push(item);
    } else dropdownItems.push(item);
  });

  return {
    priorityItems: sortByOrder(priorityItems, primaryKeys),
    dropdownItems: sortByOrder(dropdownItems, secondaryKeys),
  };
};
